import React from "react";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import BasicBadge, {BadgeStyle} from "@components/BasicBadge";
import {badgeStyle} from "@commons/components/BasicBadge";
import {TurnoRecord} from "@commons/mapping/TurnoRecord";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {Calc} from "@commons/mapping/Calc";
import TooltipEx from "@components/Tooltip";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectIdAzienda} from "@commons/core.slice";
import {Utils} from "@commons/mapping/PresenzeGiornataPlusRecord.pianificazione";

type Parts = { ore: number, frazione: number };

function formatFrazione(frazione: number): string {
    if (frazione === 0)
        return '';
    else return ':' + (frazione < 10 ? '0' + frazione : '' + frazione);
}

interface BadgeTurnoByDay {
    day: PresenzeGiornataPlusRecord,
    turni: TurnoRecord[],
    giustificativi: GiustificativoRecord[]
}

export const BadgeTurnoByDay: React.FC<BadgeTurnoByDay> = ({day, turni, giustificativi}) => {
    const idAzienda = useAppSelector(selectIdAzienda);
    if (!turni)
        return <></>;
    const result = Utils.getPrevistoPianificazione(idAzienda, day, giustificativi);
    if (!result.hasErrorFuoriSede && !result.hasErrorMancante && !result.hasErrorEccedente
        && !result.hasFuoriSede && !result.hasMancante && !result.hasEccedente) {
        return <span className="text-xs">
            {day.previsteLocked && badgeTurno(turni.find(t => t.codTurno === day.codTurno), day.idGiorno)}
            {!day.previsteLocked && badgeTurnoCustom(day.previste!)}
            </span>;
    } else if (result.hasErrorFuoriSede || result.hasErrorMancante || result.hasErrorEccedente) {
        const descrizione = day.previste ? getDescrizioneTurno(day.previste) : null;
        const tooltip = <><p className="text-center">Previsto: {descrizione}</p>
            <ul>
                {result.hasErrorFuoriSede && <li>Intervalli fuori sede non coincidenti con previsto</li>}
                {result.hasErrorMancante && <li>Intervalli mancanti non coincidenti con previsto</li>}
                {result.hasErrorEccedente && <li>Intervalli eccedente coincidenti con previsto</li>}
            </ul>
        </>;
        return <TooltipEx tooltip={tooltip}>
            <div className="min-w-[80px] max-w-[80px] rounded bg-anomalia text-xs px-1">
                {day.previsteLocked && badgeTurno(turni.find(t => t.codTurno === day.codTurno), day.idGiorno)}
                {!day.previsteLocked && badgeTurnoCustomSenzaTooltip(descrizione)}
            </div>
        </TooltipEx>;
    } else {
        let style: BadgeStyle = {bg: badgeStyle.warning.bg, text: badgeStyle.warning.bg};
        const turno = turni.find(t => t.codTurno === day.codTurno);
        if (turno && day.idGiorno - 1 < turno.distribuzione.length) {
            if (turno.distribuzione[day.idGiorno - 1].bgColor)
                style = {bg: turno.distribuzione[day.idGiorno - 1].bgColor!, text: 'text-black'};
        }
        return <span className="text-xs">{badgeTurnoCustom(result.intervalli!, style)}</span>;
    }
}

const badgeTurno: (turno: TurnoRecord | undefined, idGiorno: number) => (null | JSX.Element) = (turno: TurnoRecord | undefined, idGiorno: number) => {
    if (!turno)
        return null;

    let descrizione: string;
    let style: BadgeStyle = {bg: badgeStyle.primary.bg, text: 'text‑current'};
    if (idGiorno - 1 < turno.distribuzione.length) {
        descrizione = turno.distribuzione[idGiorno - 1].descrizione;
        if (turno.distribuzione[idGiorno - 1].bgColor)
            style = {bg: turno.distribuzione[idGiorno - 1].bgColor!, text: 'text-black'};
    } else
        descrizione = '??-??';

    return <BasicBadge as={"div"}
                       label={descrizione} type={"custom"} customStyle={style}
                       containerClass="mx-auto max-w-fit min-w-[60px] text-ellipsis overflow-hidden whitespace-nowrap"
    ></BasicBadge>;
}

export const getDescrizioneTurno = (coppie: CoppiaTimbrature[]) => {
    let descrizione: string = '';
    coppie.forEach(coppia => {
        if (!CoppiaTimbrature.isNull(coppia)) {
            if (descrizione.length > 0) {
                descrizione += ', ';
            }
            {
                const {ore, frazione} = Calc.getParts(Number.parseInt(coppia.entrata || '0', 10)) as Parts;
                descrizione += ore + formatFrazione(frazione) + '-';
            }
            {
                const {ore, frazione} = Calc.getParts(Number.parseInt(coppia.uscita || '0', 10)) as Parts;
                descrizione += ore + formatFrazione(frazione);
            }
        }
    });
    return descrizione;
}

const badgeTurnoCustom: (coppie: CoppiaTimbrature[], style?: BadgeStyle) => (null | JSX.Element) = (coppie: CoppiaTimbrature[], style: BadgeStyle = badgeStyle.warning) => {
    if (!coppie)
        return null;

    const descrizione: string = getDescrizioneTurno(coppie);
    if (descrizione) {
        return <TooltipEx tooltip={descrizione}>
            <BasicBadge as={"div"}
                        label={descrizione}
                        type={"custom"} customStyle={style}
                        containerClass={`mx-auto border border-gray-500 !py-px !text-[10px] max-w-fit min-w-[60px] text-ellipsis overflow-hidden whitespace-nowrap`}/>
        </TooltipEx>;
    } else {
        return <>-</>
    }
}

const badgeTurnoCustomSenzaTooltip: (descrizione: string | null, style?: BadgeStyle) => JSX.Element = (descrizione: string | null, style: BadgeStyle = badgeStyle.warning) => {

    if (descrizione) {
        return <BasicBadge as={"div"}
                           label={descrizione}
                           type={"custom"} customStyle={style}
                           containerClass={`mx-auto border border-gray-500 !py-px !text-[10px] max-w-fit min-w-[60px] text-ellipsis overflow-hidden whitespace-nowrap`}/>
    } else {
        return <>-</>
    }
}

export const BadgeTurno: React.FC<{ elemento: TurnoRecord | undefined, idGiorno?: number }> = ({elemento, idGiorno = 1}) => <>{badgeTurno(elemento, idGiorno)}</>
